import { useRequestCertifyProfile, useRequestGetCertifications } from '@/hooks/httpRequest';
import type { VerificationAdditionalCheckStatusEnum, VerificationStateEnum } from '@/enums';
import { Loader, PanelInfo, UserCertificationItem } from '@/components/custom';
import type { IHooksUseRequestBanUserParams, IHooksUseRequestEditVerificationStateParams } from '@/interfaces';
import { AdminUpdateActionEnum, VerifStatusEnum } from '@/lib/enum.ts';
import { useInvalidateQueries, useListenEvents } from '@/hooks';
import type { ICertifications } from '@/lib/interface.ts';
import type { GenderEnum } from '@/lib/enum.ts';
import { notifyInternalMessage } from '@/functions';
import { useEffect, useState } from 'react';
import { PartyPopper } from 'lucide-react';
import { Button } from '@/components/ui';
import routes from '@/lib/routes';

type TProps = {
	additionalCheckStatus?: VerificationAdditionalCheckStatusEnum;
	additionalCheckState?: VerificationStateEnum;
	filter?: (value: ICertifications) => boolean;
	deniedStateChange?: boolean;
	gender?: GenderEnum;
	readonly?: boolean;
	run?: boolean;
	disableActionInvalidations?: boolean;
};

interface IState {
	verifiedCertificationIds: string[];
	certifications: ICertifications[];
}

type TUserData = ICertifications['userData'];
type TUserDataWithExtra = TUserData & {
	extra?: {
		certificationRequired?: boolean;
	};
};

const CONSTS = {
	initState: {
		verifiedCertificationIds: [],
		certifications: []
	} as IState
};

const CertificationsContainer = ({
	run = true,
	readonly = false,
	gender,
	additionalCheckStatus,
	additionalCheckState,
	deniedStateChange,
	filter,
	disableActionInvalidations = false
}: TProps) => {
	const { certifications, isLoadingCertifications } = useRequestGetCertifications(
		{
			additionalCheckStatus,
			additionalCheckState,
			gender
		},
		run
	);
	const { triggerCertification } = useRequestCertifyProfile({ disableInvalidateQueries: true });
	const [state, setState] = useState<IState>(CONSTS.initState);
	const { invalidate } = useInvalidateQueries();

	const withCertificationRequired = (userData: TUserData): TUserData => {
		const casted = userData as TUserDataWithExtra;
		return {
			...casted,
			extra: {
				...(casted.extra ?? {}),
				certificationRequired: true
			}
		} as TUserData;
	};

	const updateUserDataById = (userId: string, updater: (userData: TUserData) => TUserData) => {
		setState(l => ({
			...l,
			certifications: l.certifications.map(item =>
				item.userData?._id !== userId
					? item
					: {
							...item,
							userData: updater(item.userData)
						}
			)
		}));
	};

	useListenEvents({ type: 'userCoinsEdited', callback: () => invalidate() });
	useListenEvents({ type: 'userDeleted', callback: () => invalidate() });
	useListenEvents<{ userId?: string; newGender?: GenderEnum }>({
		type: 'userGenderChangedOptimistic',
		callback: ({ userId, newGender }) => {
			if (!disableActionInvalidations || !userId || !newGender) {
				return;
			}

			updateUserDataById(userId, userData => ({ ...userData, gender: newGender }));
		}
	});
	useListenEvents<{ userId?: string }>({
		type: 'userCertificationRequiredOptimistic',
		callback: ({ userId }) => {
			if (!disableActionInvalidations || !userId) {
				return;
			}

			updateUserDataById(userId, withCertificationRequired);
		}
	});
	useListenEvents({
		type: 'userEdited',
		callback: ({ userId, action }: { userId?: string; action?: AdminUpdateActionEnum }) => {
			if (!disableActionInvalidations) return invalidate();
			if (!userId || !action) return;

			setState(prev => ({
				...prev,
				certifications: prev.certifications.map(item => {
					if (item.userData?._id !== userId) return item;

					switch (action) {
						case AdminUpdateActionEnum.REQUEST_CERTIF:
							return { ...item, userData: withCertificationRequired(item.userData) };
						case AdminUpdateActionEnum.REMOVE_BIO:
							return {
								...item,
								userData: {
									...item.userData,
									verifications: { ...item.userData.verifications, bio: { status: VerifStatusEnum.DENIED } }
								}
							};
						default:
							return item;
					}
				})
			}));
		}
	});
	useListenEvents({
		type: 'userWarned',
		callback: () => {
			if (!disableActionInvalidations) {
				invalidate();
			}
		}
	});
	useListenEvents<IHooksUseRequestBanUserParams>({
		type: 'userBanned',
		callback: data => {
			setState(l => ({
				...l,
				certifications: l.certifications.filter(item => item.userData?._id !== data.userId)
			}));
			invalidate();
		}
	});
	useListenEvents({
		type: 'verificationStateChanged',
		callback: (data: IHooksUseRequestEditVerificationStateParams) =>
			setState(l => ({
				...l,
				certifications: [...l.certifications].filter(item => item._id !== data.verificationId)
			}))
	});

	useEffect(() => {
		setState({
			...CONSTS.initState,
			certifications: [...certifications].filter(filter ?? (() => true))
		});
	}, [certifications, filter]);

	const handleCertification = (certif: ICertifications, accept: boolean) => {
		let shouldInvalidateAfterSuccess = false;
		setState(prevState => {
			const newState = {
				...prevState,
				certifications: prevState.certifications.filter(ll => ll._id !== certif._id),
				verifiedCertificationIds: [...prevState.verifiedCertificationIds, certif._id]
			};
			shouldInvalidateAfterSuccess = newState.verifiedCertificationIds.length !== 0 && newState.certifications.length === 0;
			return newState;
		});
		// Scroll to first element of list
		window.scroll({ left: 0, top: 0, behavior: 'smooth' });
		// Sync verification with server
		triggerCertification({
			accept,
			gender: gender ?? certif.userData.gender,
			code: certif.verificationCode,
			newGender: certif.newGender
		})
			.then(() => {
				shouldInvalidateAfterSuccess && invalidateQueries();
			})
			.catch(notifyInternalMessage);
	};

	const removeItemLocally = (userId: string) => {
		setState(l => ({
			...l,
			certifications: l.certifications.filter(item => item.userData?._id !== userId)
		}));
		window.scroll({ left: 0, top: 0, behavior: 'smooth' });
	};

	const invalidateQueries = () =>
		Promise.all([invalidate(routes.api.verifications.count), invalidate(routes.api.certifications.base)]);

	if (isLoadingCertifications) {
		return (
			<div className="my-10">
				<Loader className="my-10" />
			</div>
		);
	}

	if (state.certifications.length === 0) {
		return (
			<PanelInfo
				title="Terminé!"
				description={`Vous n'avez actuellement aucune certification disponible`}
				className="py-10 md:py-28 border-none"
				iconClassName="text-green-500"
				icon={PartyPopper}
			>
				<Button onClick={invalidateQueries} variant={'secondary'} type="button">
					Actualiser la page
				</Button>
			</PanelInfo>
		);
	}

	return (
		<div className="flex flex-wrap justify-center gap-3 md:gap-4">
			{state.certifications.filter(l => !!l.userData).map(l => (
				<div key={l._id}>
					<UserCertificationItem
						deniedStateChange={deniedStateChange}
						onClick={handleCertification}
						removeItemLocally={removeItemLocally}
						disableActionInvalidations={disableActionInvalidations}
						className="w-full md:w-auto"
						readonly={readonly}
						item={l}
					/>
				</div>
			))}
		</div>
	);
};

export default CertificationsContainer;
